import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import "./SpreadsheetView.css";
import FilterBar from "./FilterBar";
import ExportButton from "./ExportButton";

// Get backend URL based on environment
const getBackendURL = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  const hostname = window.location.hostname;
  const runningLocally = hostname === "localhost" || hostname === "127.0.0.1";
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;

  if (!runningLocally) {
    return `https://${hostname}`;
  }

  switch (env) {
    case "staging":
      return "https://staging.cafuamanagement.com";
    case "production":
      return "https://peopleplan.cafuamanagement.com";
    default:
      return "http://localhost:5000";
  }
};

const SpreadsheetView = ({ selectedMUM, onMUMChange }) => {
  // State Management
  const [matrixData, setMatrixData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState("");
  const [stores, setStores] = useState([]);
  const [positionStatuses, setPositionStatuses] = useState({});
  const [maxSLCount, setMaxSLCount] = useState(2);
  const [totalCounts, setTotalCounts] = useState({
    stores: 0,
    rm: 0,
    am: 0,
    sl: 0,
    totalEmployees: 0,
    vacantPositions: 0,
  });

  const tableRef = useRef(null);

  // Helper function to check if date is past due
  const isDatePastDue = (dateString) => {
    if (!dateString) return false;
    const targetDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);
    return targetDate < today;
  };

  // Helper function to get days overdue
  const getDaysOverdue = (dateString) => {
    if (!dateString) return 0;
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = today - targetDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Fetch matrix data
  const fetchMatrixData = useCallback(async () => {
    if (!selectedMUM && !selectedStore) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${getBackendURL()}/api/store-positions-matrix?${
          selectedMUM ? `mum=${encodeURIComponent(selectedMUM)}` : ""
        }&${selectedStore ? `store=${encodeURIComponent(selectedStore)}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch matrix data");
      }

      const data = await response.json();

      // Process data to separate SLs into individual positions
      const processedData = processStoreData(data.stores || []);
      setMatrixData(processedData);
      setFilteredData(processedData);

      // Calculate max SL positions needed
      const maxSLs = Math.max(
        2,
        ...processedData.map((store) => store.shift_leaders?.length || 0)
      );
      setMaxSLCount(Math.min(maxSLs, 5)); // Cap at 5 SL columns

      // Calculate totals
      calculateTotals(processedData);
    } catch (error) {
      console.error("Error fetching matrix data:", error);
      toast.error("Failed to load spreadsheet data");
    } finally {
      setLoading(false);
    }
  }, [selectedMUM, selectedStore]);

  // Process store data to separate SLs
  const processStoreData = (stores) => {
    return stores.map((store) => {
      // Parse SL details into individual positions
      const slPositions = [];
      if (store.sl_details && Array.isArray(store.sl_details)) {
        store.sl_details.forEach((sl, index) => {
          slPositions[index] = {
            position_index: index + 1,
            employee: {
              user_id: sl.user_id,
              name: sl.name,
              tenure_years: sl.tenure_years,
              tenure_months: sl.tenure_months,
            },
          };
        });
      }

      return {
        ...store,
        shift_leaders: slPositions,
      };
    });
  };

  // Fetch stores for dropdown
  const fetchStores = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${getBackendURL()}/api/stores-by-mum${
          selectedMUM ? `?mum=${encodeURIComponent(selectedMUM)}` : ""
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch stores");
      }

      const data = await response.json();
      setStores(data.stores || []);
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [selectedMUM]);

  // Fetch position statuses - READ ONLY
  const fetchPositionStatuses = useCallback(async () => {
    if (!selectedMUM) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${getBackendURL()}/api/position-statuses?mum=${encodeURIComponent(
          selectedMUM
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch position statuses");
      }

      const data = await response.json();
      setPositionStatuses(data.position_statuses || {});
    } catch (error) {
      console.error("Error fetching position statuses:", error);
    }
  }, [selectedMUM]);

  // Calculate totals
  const calculateTotals = (data) => {
    const counts = {
      stores: data.length,
      rm: 0,
      am: 0,
      sl: 0,
      totalEmployees: 0,
      vacantPositions: 0,
    };

    data.forEach((store) => {
      if (store.rm) counts.rm++;
      else counts.vacantPositions++;

      if (store.am) counts.am++;
      else counts.vacantPositions++;

      const slCount = store.shift_leaders?.length || 0;
      counts.sl += slCount;
      counts.totalEmployees +=
        (store.rm ? 1 : 0) + (store.am ? 1 : 0) + slCount;
    });

    setTotalCounts(counts);
  };

  // Handle store filter change
  const handleStoreChange = (storeName) => {
    setSelectedStore(storeName);

    if (!storeName) {
      setFilteredData(matrixData);
      calculateTotals(matrixData);
    } else {
      const filtered = matrixData.filter(
        (store) => store.restaurant_name === storeName
      );
      setFilteredData(filtered);
      calculateTotals(filtered);
    }
  };

  // Get position status
  const getPositionStatus = (storeCorp, positionType, positionIndex = null) => {
    const key = positionIndex
      ? `${positionType}_${positionIndex}`
      : positionType;
    return positionStatuses[storeCorp]?.[key] || null;
  };

  // Export to Excel
  const handleExport = () => {
    const exportData = filteredData.map((store) => {
      const row = {
        "Restaurant Name": store.restaurant_name,
        "RM (In Position)": store.rm ? store.rm.name : "",
        "AM (In Position)": store.am ? store.am.name : "",
      };

      // Add SL columns
      for (let i = 1; i <= maxSLCount; i++) {
        const sl = store.shift_leaders?.find((s) => s.position_index === i);
        row[`SL ${i} Full Name (In Position)`] = sl ? sl.employee.name : "";
      }

      // Add training columns
      const trainingStatuses = [];
      const trainingDates = [];

      // Check each position for training status
      ["rm", "am"].forEach((pos) => {
        const status = getPositionStatus(store.restaurant_name, pos);
        if (status?.status === "in_training") {
          trainingStatuses.push(`${pos.toUpperCase()}: ${status.trainee_name}`);
          if (status.training_date) {
            trainingDates.push(status.training_date);
          }
        }
      });

      for (let i = 1; i <= maxSLCount; i++) {
        const status = getPositionStatus(store.restaurant_name, "sl", i);
        if (status?.status === "in_training") {
          trainingStatuses.push(`SL${i}: ${status.trainee_name}`);
          if (status.training_date) {
            trainingDates.push(status.training_date);
          }
        }
      }

      row["In Training"] = trainingStatuses.join(", ") || "";
      row["In Training Goal Certification Day"] = trainingDates[0] || "";

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "People Plan Matrix");

    // Auto-size columns
    const maxWidths = {};
    exportData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const length = String(row[key]).length;
        maxWidths[key] = Math.max(maxWidths[key] || 10, length + 2);
      });
    });

    ws["!cols"] = Object.keys(maxWidths).map((key) => ({
      wch: maxWidths[key],
    }));

    XLSX.writeFile(
      wb,
      `PeoplePlan_Matrix_${selectedMUM}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
    toast.success("Excel file downloaded");
  };

  // Format tenure
  const formatTenure = (years, months) => {
    if (years === undefined && months === undefined) return "";
    return `${years || 0}y ${months || 0}m`;
  };

  // Effects
  useEffect(() => {
    fetchMatrixData();
    fetchStores();
    fetchPositionStatuses();
  }, [selectedMUM, fetchMatrixData, fetchStores, fetchPositionStatuses]);

  useEffect(() => {
    if (selectedStore) {
      fetchMatrixData();
    }
  }, [selectedStore, fetchMatrixData]);

  // Render position cell - READ ONLY VERSION
  const renderPositionCell = (
    store,
    positionType,
    positionIndex = null,
    employee = null
  ) => {
    const status = getPositionStatus(
      store.restaurant_name,
      positionType,
      positionIndex
    );

    const hasEmployee = employee && employee.name;
    const isVacant = status?.status === "vacant";
    const isInTraining = status?.status === "in_training";
    const isPastDue = isInTraining && isDatePastDue(status?.training_date);
    const daysOverdue = isPastDue ? getDaysOverdue(status.training_date) : 0;

    return (
      <td
        className={`position-cell ${
          hasEmployee ? "has-employee" : "empty-position"
        } ${isVacant ? "vacant" : ""} ${isInTraining ? "in-training" : ""} ${
          isPastDue ? "past-due" : ""
        }`}
      >
        <div className="position-cell-content">
          {hasEmployee && (
            <div className="employee-info">
              <span className="employee-name">{employee.name}</span>
              {employee.tenure_years !== undefined && (
                <span className="employee-tenure">
                  {formatTenure(employee.tenure_years, employee.tenure_months)}
                </span>
              )}
            </div>
          )}

          {!hasEmployee && !status && (
            <span className="empty-indicator">—</span>
          )}

          {status && (
            <div className="status-info">
              {isVacant && <span className="status-badge vacant">Vacant</span>}
              {isInTraining && (
                <>
                  <span className="status-badge training">
                    Training: {status.trainee_name}
                  </span>
                  {status.training_date && (
                    <div className="training-date-wrapper">
                      <span 
                        className={`training-date ${
                          isPastDue ? 'past-due' : ''
                        } ${daysOverdue > 7 ? 'critical-past-due' : ''}`}
                      >
                        Target: {new Date(status.training_date).toLocaleDateString()}
                      </span>
                      {isPastDue && (
                        <span className="training-date-tooltip">
                          {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
              {status.comments && (
                <span className="status-comments">{status.comments}</span>
              )}
            </div>
          )}
        </div>
      </td>
    );
  };

  // Render loading state
  if (loading && matrixData.length === 0) {
    return (
      <div className="spreadsheet-loading">
        <div className="loading-spinner"></div>
        <p>Loading spreadsheet data...</p>
      </div>
    );
  }

  return (
    <div className="spreadsheet-view-container">
      {/* Header Section */}
      <div className="spreadsheet-header">
        <div className="header-left">
          <h2>PIC Tracker (Read-Only)</h2>
          <div className="totals-summary">
            <span className="total-badge">Stores: {totalCounts.stores}</span>
            <span className="total-badge">
              Total Employees: {totalCounts.totalEmployees}
            </span>
            <span className="total-badge">RM: {totalCounts.rm}</span>
            <span className="total-badge">AM: {totalCounts.am}</span>
            <span className="total-badge">SL: {totalCounts.sl}</span>
            {totalCounts.vacantPositions > 0 && (
              <span className="total-badge vacant">
                Vacant Positions: {totalCounts.vacantPositions}
              </span>
            )}
          </div>
        </div>
        <div className="header-right">
          <FilterBar
            selectedMUM={selectedMUM}
            onMUMChange={onMUMChange}
            selectedStore={selectedStore}
            onStoreChange={handleStoreChange}
            stores={stores}
          />
          <ExportButton
            onClick={handleExport}
            disabled={filteredData.length === 0}
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="spreadsheet-table-wrapper" ref={tableRef}>
        <table className="spreadsheet-table">
          <thead>
            <tr>
              <th className="sticky-column">Restaurant Name</th>
              <th>RM (In Position)</th>
              <th>AM (In Position)</th>
              {[...Array(maxSLCount)].map((_, slIndex) => (
                <th key={`sl-header-${slIndex}`}>
                  SL {slIndex + 1} Full Name (In Position)
                </th>
              ))}
              <th>In Training</th>
              <th>In Training Goal Certification Day</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((store, index) => {
              // Collect training info for display columns
              const trainingInfo = [];
              const trainingDates = [];

              // Check all positions for training status
              const rmStatus = getPositionStatus(store.restaurant_name, "rm");
              const amStatus = getPositionStatus(store.restaurant_name, "am");

              if (rmStatus?.status === "in_training") {
                trainingInfo.push(rmStatus.trainee_name);
                if (rmStatus.training_date)
                  trainingDates.push(rmStatus.training_date);
              }
              if (amStatus?.status === "in_training") {
                trainingInfo.push(amStatus.trainee_name);
                if (amStatus.training_date)
                  trainingDates.push(amStatus.training_date);
              }

              for (let i = 1; i <= maxSLCount; i++) {
                const slStatus = getPositionStatus(
                  store.restaurant_name,
                  "sl",
                  i
                );
                if (slStatus?.status === "in_training") {
                  trainingInfo.push(slStatus.trainee_name);
                  if (slStatus.training_date)
                    trainingDates.push(slStatus.training_date);
                }
              }

              return (
                <tr key={`${store.restaurant_name}-${index}`}>
                  <td className="sticky-column restaurant-name">
                    {store.restaurant_name}
                  </td>

                  {/* RM Cell */}
                  {renderPositionCell(store, "rm", null, store.rm)}

                  {/* AM Cell */}
                  {renderPositionCell(store, "am", null, store.am)}

                  {/* SL Cells */}
                  {[...Array(maxSLCount)].map((_, slIndex) => {
                    const sl = store.shift_leaders?.find(
                      (s) => s.position_index === slIndex + 1
                    );
                    return renderPositionCell(
                      store,
                      "sl",
                      slIndex + 1,
                      sl?.employee || null
                    );
                  })}

                  {/* In Training */}
                  <td className="training-info-cell">
                    {trainingInfo.length > 0 ? trainingInfo.join(", ") : "—"}
                  </td>

                  {/* Goal Certification Day */}
                  <td className={`training-date-cell ${
                    trainingDates.length > 0 && isDatePastDue(trainingDates[0]) ? 'past-due' : ''
                  }`}>
                    {trainingDates.length > 0 ? (
                      <span className={`date-text ${isDatePastDue(trainingDates[0]) ? 'past-due' : ''}`}>
                        {new Date(trainingDates[0]).toLocaleDateString()}
                        {isDatePastDue(trainingDates[0]) && (
                          <span className="overdue-label">OVERDUE</span>
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}

            {filteredData.length === 0 && (
              <tr>
                <td colSpan={maxSLCount + 5} className="no-data">
                  {selectedMUM || selectedStore
                    ? "No stores found for selected filters"
                    : "Please select a MUM or Store to view data"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpreadsheetView;